import React, {
    useCallback,
    useEffect,
    type ReactNode,
    type InputHTMLAttributes,
    type ChangeEvent
} from 'react';
import classnames from 'classnames/bind';
import { isNil, isNumber, noop, toNumber, toString } from 'lodash/fp';
import { useDefaultId } from 'app/lib/hooks';
import { CharacterCount } from 'app/components/core/CharacterCount';
import { FieldLabel } from 'app/components/core/FieldLabel';
import { useErrorTooltip } from 'app/components/core/ErrorTooltip';
import { useTheme } from 'app/components/core/ThemeProvider';
import { Actions } from './Actions';
import * as styles from './Input.css';

const cx = classnames.bind(styles);

export enum InputVariant {
    OUTLINE = 'outline',
    FILL = 'fill',
    UNDERLINE = 'underline'
}

export enum InputSize {
    SMALL = 'small',
    MEDIUM = 'medium',
    LARGE = 'large',
    EXTRA_LARGE = 'extra-large'
}

const VARIANT_CLASSES: Record<InputVariant, string> = {
    [InputVariant.OUTLINE]: 'variant-outline',
    [InputVariant.FILL]: 'variant-fill',
    [InputVariant.UNDERLINE]: 'variant-underline'
};

const SIZE_CLASSES: Record<InputSize, string> = {
    [InputSize.SMALL]: 'size-sm',
    [InputSize.MEDIUM]: 'size-md',
    [InputSize.LARGE]: 'size-lg',
    [InputSize.EXTRA_LARGE]: 'size-xl'
};

type InputType = 'text' | 'password' | 'number' | 'email' | 'tel' | 'url' | 'search';

function cast(type: string, value: string): string | number | null {
    switch (type) {
        case 'number':
            return value === '' ? null : toNumber(value);
        case 'text':
        case 'password':
            return toString(value);
        default:
            return value;
    }
}

const useEventCallback = (type: string, callback: (value: string | number | null) => void) =>
    useCallback(
        (event: ChangeEvent<HTMLInputElement>) => callback(cast(type, event.target.value)),
        [type, callback]
    );

export interface InputProps extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'size' | 'type' | 'onChange'
> {
    testId?: string;
    name?: string;
    id?: string;
    disabled?: boolean;
    label?: ReactNode;
    value?: string | number;
    maxLength?: number;
    showCharCount?: boolean;
    onChange?: (value: string | number | null) => void;
    type?: InputType;
    error?: string | string[];
    warning?: string | string[];
    variant?: InputVariant;
    size?: InputSize;
    autoFocus?: boolean;
    tooltip?: string;
    children?: ReactNode;
}

export function Input({
    testId,
    className,
    id: suppliedId,
    name,
    value = '',
    disabled = false,
    label,
    onChange = noop,
    type = 'text',
    error,
    warning,
    variant = InputVariant.OUTLINE,
    size = InputSize.LARGE,
    maxLength,
    showCharCount = false,
    autoFocus = false,
    tooltip,
    children,
    ...props
}: InputProps) {
    const { themeClassname } = useTheme();
    const id = useDefaultId('input', suppliedId);
    const handleChange = useEventCallback(type, onChange);

    const hasError = Boolean(error?.length);
    const hasWarning = !hasError && Boolean(warning?.length);

    const classes = cx(
        'base',
        themeClassname,
        VARIANT_CLASSES[variant],
        SIZE_CLASSES[size],
        { hasError, hasWarning },
        className
    );

    const { ErrorTooltip, refs, getReferenceProps, getTooltipProps } = useErrorTooltip();
    const inputRef = refs.reference as React.RefObject<HTMLInputElement | null>;

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    }, [inputRef, autoFocus]);

    return (
        <div className={classes} data-testid={testId}>
            <FieldLabel htmlFor={id} tooltip={tooltip}>
                {label}
            </FieldLabel>
            <div className={styles.wrapper}>
                <input
                    autoComplete="off"
                    type={type}
                    id={id}
                    name={name}
                    disabled={disabled}
                    className={styles.input}
                    value={isNil(value) ? '' : toString(value)}
                    maxLength={maxLength}
                    onChange={handleChange}
                    aria-invalid={hasError || hasWarning}
                    {...getReferenceProps(props)}
                />
                {children && <Actions inputRef={inputRef}>{children}</Actions>}
                <ErrorTooltip error={error} warning={warning} {...getTooltipProps()} />
            </div>
            {showCharCount && isNumber(maxLength) ? (
                <CharacterCount
                    className={styles.count}
                    limit={maxLength}
                    value={toString(value).length}
                />
            ) : null}
        </div>
    );
}
